import { useIsomorphicLayoutEffect } from '@/shared/hooks/useIsomorphicLayoutEffect';

/** Счётчик вложенных блокировок: sheet поверх modal не должен разблокировать body. */
let locks = 0;
const LOCKED_PROPERTIES = ['position', 'top', 'left', 'width', 'overflow', 'padding-right'] as const;
let savedStyles: Array<{ property: string; value: string; priority: string }> = [];
let savedScrollX = 0;
let savedScrollY = 0;

/**
 * Блокировка скролла body под оверлеем.
 *
 * `overflow: hidden` на body в iOS WebView не работает — страница продолжает
 * «резиниться». Единственный надёжный способ — position: fixed с сохранением
 * позиции скролла и её восстановлением.
 */
export function useBodyScrollLock(active: boolean): void {
  useIsomorphicLayoutEffect(() => {
    if (!active) return;
    const body = document.body;

    locks += 1;
    if (locks === 1) {
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const paddingRight = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      savedScrollX = window.scrollX;
      savedScrollY = window.scrollY;
      savedStyles = LOCKED_PROPERTIES.map((property) => ({
        property,
        value: body.style.getPropertyValue(property),
        priority: body.style.getPropertyPriority(property),
      }));
      body.style.position = 'fixed';
      body.style.top = `-${savedScrollY}px`;
      body.style.left = `-${savedScrollX}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      // Global border-box keeps the content width stable when the scrollbar disappears.
      if (scrollbarWidth > 0) body.style.paddingRight = `${paddingRight + scrollbarWidth}px`;
    }

    return () => {
      locks = Math.max(0, locks - 1);
      if (locks === 0) {
        for (const { property, value, priority } of savedStyles) {
          if (value) body.style.setProperty(property, value, priority);
          else body.style.removeProperty(property);
        }
        // The root uses scroll-behavior:smooth; restoring a fixed body must
        // never animate the page from the top back to its previous position.
        window.scrollTo({ left: savedScrollX, top: savedScrollY, behavior: 'instant' });
        savedStyles = [];
      }
    };
  }, [active]);
}
